import { assertNever } from "@@/shared/assert-never";
import { sortBy } from "@@/utilities/sort";
import type { PayRequestEvent } from "../../events/_pay-request.event";
import { firstEventMustBeACreatedEventCheck } from "../../fist-event-must-be-a-created-event-check";
import { missingPayRequestEventCheck } from "../../missing-pay-request-event-check";
import { noEventsForPayRequestCheck } from "../../no-events-for-pay-request-check";
import type { PayRequestId } from "../../pay-request-id";
import type { PayRequestForCheckoutProjection } from "./pay-request.for-checkout.projection";
import { payRequestForCheckoutProjectionZodObject } from "./pay-request.for-checkout.projection";

const payRequestForCheckoutProjectionReducer = (
    payRequestId: PayRequestId,
    events: PayRequestEvent[],
): PayRequestForCheckoutProjection | undefined => {
    const _events = events
        .filter(event => event.payRequestId === payRequestId)
        .sort(sortBy(event => event._version));

    // Verifiera att det finns givna events för angivet payRequestId
    noEventsForPayRequestCheck(payRequestId, _events);

    // Make sure the first event is PAY_REQUEST_CREATED_EVENT
    firstEventMustBeACreatedEventCheck(payRequestId, _events);

    // Verifiera att det inte finns några luckor mellan versionsnumren
    missingPayRequestEventCheck(payRequestId, _events);

    let projection = {
        _id: payRequestId,
        _type: "PAY_REQUEST_FOR_CHECKOUT_PROJECTION",
    } as PayRequestForCheckoutProjection;

    projectionLoop: for (const event of _events) {
        switch (event.type) {
            case "PAY_REQUEST_CREATED_EVENT": {
                const _projection: PayRequestForCheckoutProjection = {
                    _id: event.payRequestId,
                    _type: "PAY_REQUEST_FOR_CHECKOUT_PROJECTION",
                    _createdAt: event._createdAt,
                    _version: event._version,
                    checkoutable: true,
                    paymentFulfilled: false,
                    paymentInitiated: false,
                    recipients: event.data.recipients,
                    paymentEvents: [],
                    price: event.data.price,
                    items: event.data.items,
                    paymentFor: event.paymentFor,
                };
                projection = _projection;
                break;
            }
            case "PAY_REQUEST_CANCELLED_EVENT": {
                const _projection: PayRequestForCheckoutProjection = {
                    ...projection,
                    _version: event._version,
                    checkoutable: false,
                };
                projection = _projection;
                break;
            }
            case "PAY_REQUEST_PAYMENT_CANCELLED_EVENT": {
                const _projection: PayRequestForCheckoutProjection = {
                    ...projection,
                    _version: event._version,
                    checkoutable: true,
                };
                _projection.paymentEvents.push(event);
                projection = _projection;
                break;
            }
            case "PAY_REQUEST_PAYMENT_EXPIRED_EVENT": {
                const _projection: PayRequestForCheckoutProjection = {
                    ...projection,
                    _version: event._version,
                    checkoutable: true,
                };
                _projection.paymentEvents.push(event);
                projection = _projection;
                break;
            }
            case "PAY_REQUEST_PAYMENT_FULFILLED_EVENT": {
                const _projection: PayRequestForCheckoutProjection = {
                    ...projection,
                    _version: event._version,
                    checkoutable: false,
                    paymentFulfilled: true,
                };
                _projection.paymentEvents.push(event);
                projection = _projection;
                // If the payment is fulfilled, we can break the loop
                // as we don't need to process any more events
                // since the payment has already been fulfilled
                break projectionLoop;
            }
            case "PAY_REQUEST_PAYMENT_INITIATED_EVENT": {
                const _projection: PayRequestForCheckoutProjection = {
                    ...projection,
                    _version: event._version,
                    checkoutable: false,
                    paymentInitiated: true,
                };
                _projection.paymentEvents.push(event);
                projection = _projection;
                break;
            }
            case "PAY_REQUEST_PAYMENT_FAILED_EVENT": {
                const _projection: PayRequestForCheckoutProjection = {
                    ...projection,
                    _version: event._version,
                    paymentInitiated: false,
                    checkoutable: true,
                };
                _projection.paymentEvents.push(event);
                projection = _projection;
                break;
            }
            default:
                assertNever(event, "Unhandled event type", {
                    valueProp: "type",
                });
        }
    }
    // console.log("aggregation", aggregation);
    return payRequestForCheckoutProjectionZodObject.parse(projection);
};

export { payRequestForCheckoutProjectionReducer };
